import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Star, Check, X, Trash2, MessageSquare, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StarBar = ({ value }) => (
  <span className="inline-flex items-center" data-testid={`stars-${value}`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`h-4 w-4 ${n <= value ? 'fill-yellow-400 text-yellow-400' : 'fill-none text-gray-300'}`}
      />
    ))}
  </span>
);

const AdminReviews = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReviews(data.items || []);
        setCounts(data.counts || { pending: 0, approved: 0, rejected: 0 });
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    const res = await fetch(`${API_URL}/api/admin/reviews/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    if (res.ok) fetchReviews();
  };

  const deleteReview = async (id) => {
    if (!window.confirm(t('admin.reviews.confirm_delete'))) return;
    const res = await fetch(`${API_URL}/api/admin/reviews/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) fetchReviews();
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(i18n.language || 'sk');
    } catch {
      return iso;
    }
  };

  const tabs = [
    { key: 'pending', label: t('admin.reviews.tab_pending'), count: counts.pending, color: 'bg-yellow-500' },
    { key: 'approved', label: t('admin.reviews.tab_approved'), count: counts.approved, color: 'bg-green-500' },
    { key: 'rejected', label: t('admin.reviews.tab_rejected'), count: counts.rejected, color: 'bg-red-500' },
    { key: 'all', label: t('admin.reviews.tab_all'), count: counts.pending + counts.approved + counts.rejected, color: 'bg-gray-500' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-reviews-page">
      <div className="flex items-center gap-3">
        <MessageSquare className="h-7 w-7 text-orange-500" />
        <h1 className="text-3xl font-bold">{t('admin.reviews.title')}</h1>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 font-medium transition relative ${
              filter === tab.key
                ? 'text-orange-600 border-b-2 border-orange-500'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid={`filter-${tab.key}`}
          >
            {tab.label}
            <span className={`ml-2 px-2 py-0.5 text-xs text-white rounded-full ${tab.color}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg" data-testid="reviews-empty">
          <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{t('admin.reviews.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-reviews-list">
          {reviews.map((r) => {
            const productName =
              (r.product_name && (r.product_name[i18n.language] || r.product_name.sk || r.product_name.en)) || r.product_sku || '—';
            return (
              <div
                key={r.id}
                className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm"
                data-testid={`admin-review-${r.id}`}
              >
                <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-semibold text-gray-900">{r.user_name || 'User'}</span>
                      <StarBar value={r.rating} />
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          r.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : r.status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {r.status === 'pending' && <Clock className="inline h-3 w-3 mr-1" />}
                        {t(`admin.reviews.status_${r.status}`)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(r.created_at)} · {t('admin.reviews.product')}:{' '}
                      <span className="font-medium text-gray-700">{productName}</span>
                      {r.product_sku && <span className="ml-1 text-gray-400">({r.product_sku})</span>}
                      <span className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 text-xs">
                        {r.language?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status !== 'approved' && (
                      <button
                        onClick={() => updateStatus(r.id, 'approved')}
                        className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition text-sm flex items-center gap-1"
                        data-testid={`approve-${r.id}`}
                      >
                        <Check className="h-4 w-4" /> {t('admin.reviews.approve')}
                      </button>
                    )}
                    {r.status !== 'rejected' && (
                      <button
                        onClick={() => updateStatus(r.id, 'rejected')}
                        className="px-3 py-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-sm flex items-center gap-1"
                        data-testid={`reject-${r.id}`}
                      >
                        <X className="h-4 w-4" /> {t('admin.reviews.reject')}
                      </button>
                    )}
                    <button
                      onClick={() => deleteReview(r.id)}
                      className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition text-sm flex items-center gap-1"
                      data-testid={`delete-${r.id}`}
                    >
                      <Trash2 className="h-4 w-4" /> {t('admin.reviews.delete')}
                    </button>
                  </div>
                </div>
                {r.comment && (
                  <p className="text-gray-700 bg-gray-50 p-3 rounded whitespace-pre-wrap">{r.comment}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
